/**
 * Login form.
 *
 * One step: e-mail and password. The prototype also drew a two-factor step, a "manter conectado"
 * checkbox and a password-recovery link — none of which exist behind the scenes (2FA and password
 * reset are unimplemented items in the Auth module's roadmap, and `LoginRequest` carries only
 * e-mail and password), so they are deliberately not rendered. See the module README.
 *
 * @module Pages/Onboard/Login
 */

import { UTButton, UTTextField } from '@group-link-one/gl-ui/utilities';
import type { JSX } from 'react';
import { TermsSection } from '../Components';
import { useLogin } from '../hooks/useLogin';
import { useLoginTranslation } from '../hooks/useLoginTranslation';
import { FormContainer, Heading, Subtitle, TitleBlock } from '../Login.styles';

const LoginFormContent = (): JSX.Element => {
  const { customLocale } = useLoginTranslation();

  const {
    register,
    handleSubmit,
    errors,
    isSubmitting,
    showPassword,
    toggleShowPassword,
    onSubmit,
  } = useLogin();

  return (
    <form className={FormContainer} onSubmit={handleSubmit(onSubmit)}>
      <div className={TitleBlock}>
        <h1 className={Heading}>Entrar</h1>
        <p className={Subtitle}>Use o e-mail corporativo GroupLink One.</p>
      </div>

      <UTTextField
        {...register('email')}
        topLabel={customLocale('email.label', 'E-mail')}
        placeholder='nome@grouplinkone.com'
        state={errors.email ? 'error' : 'normal'}
        bottomLabel={errors.email ? errors.email.message : ''}
        disabled={isSubmitting}
        size='large'
      />

      <UTTextField
        {...register('password')}
        type={showPassword ? 'text' : 'password'}
        topLabel={customLocale('password.label', 'Senha')}
        placeholder='••••••••'
        state={errors.password ? 'error' : 'normal'}
        bottomLabel={errors.password ? errors.password.message : ''}
        disabled={isSubmitting}
        rightIcon={showPassword ? 'Misc.Eye' : 'Misc.EyeClosed'}
        rightIconAction={toggleShowPassword}
        size='large'
      />

      <UTButton
        id='login-submit'
        type='submit'
        size='large'
        disabled={isSubmitting}
        text={isSubmitting ? 'Entrando...' : 'Entrar'}
      />

      <TermsSection customLocale={customLocale} />
    </form>
  );
};

export { LoginFormContent };
